// Browser-only search wrapper for the local-first BSB Bible
// (M.3.6.3.c). Lazy in-memory cache built from IDB on first call,
// then `searchVersesIn` from the pure core runs the relevance-ranked,
// typo-tolerant match (Bible-search Plan 02): strict AND-by-default,
// survivors ranked (contiguous phrase > tightest cluster > shorter
// verse > canonical order), plus an `osaDistance` fuzzy fallback that
// fires only on a zero-exact-result query. Returns a `SearchResult`
// (`{ hits, fuzzy, capped }`), each hit carrying `spans` offsets into
// its own `snippet` for slice-to-highlight.
//
// `clearSearchCache()` is called by `bsb-bootstrap.ts` after a
// successful sha-mismatch re-hydrate so the next `searchVerses`
// call rebuilds from the freshly hydrated books. Tests can call it
// too.
//
// Rejected loads clear the cache so the next call retries; only
// successful loads stay durable for the session. A transient IDB
// read failure does not poison searchVerses for the rest of the
// session.
//
// Cache miss + IDB read errors propagate as
// BibleHydrationError('idb_error') from getAllBooksFromIdb.

import { getAllBooksFromIdb } from '@/lib/bsb-db';
import {
  searchVersesIn,
  type SearchOptions,
  type SearchResult,
} from '@/lib/bsb-search-core';
import type { BsbBook } from '@/lib/bsb-types';

// Re-export so callers only need to import from this file when they
// want the browser-side wrapper plus its types.
export type {
  SearchHit,
  SearchOptions,
  SearchResult,
  VerseMatchSpan,
} from '@/lib/bsb-search-core';

let cache: Promise<BsbBook[]> | null = null;

export async function searchVerses(
  query: string,
  options: SearchOptions = {},
): Promise<SearchResult> {
  if (!cache) {
    // Wrap the rejection path so a failed first load clears the
    // cache and the next call retries fresh. Without this, a
    // transient IDB error would poison every subsequent
    // searchVerses call until clearSearchCache fires externally.
    cache = getAllBooksFromIdb().catch((err) => {
      cache = null;
      throw err;
    });
  }
  const books = await cache;
  return searchVersesIn(books, query, options);
}

export function clearSearchCache(): void {
  cache = null;
}
